import React, { useCallback, useState } from 'react';
import { Col, Row, Table } from 'reactstrap';

import { formatCurrency } from '~libs/dataUtils';
import { getErrorMessage } from '~libs/axiosWrapper';

import { Button, ContainerBanner, ErrorResponse, Spinner, SummaryStatCard } from '~components';
import { DateInput } from '~components/form-fields/inputs';
import Icon, { faExclamationTriangle, faFileCsv, faInfoCircle } from '~components/icon';

import { useLazyGetAcfrReportQuery, type AcfrReportResponse } from '~support-at-home/actions/sahReports';
import { ACFR_CATEGORY_LABELS } from '~support-at-home/lib/acfrCategory';

const DEFAULT_DATE_FROM = '2025-11-01';
const DEFAULT_DATE_TO = '2026-06-30';

const INCOME_ROWS: {
  key: keyof AcfrReportResponse['income'];
  label: string;
  showParticipant: boolean;
  showCareM: boolean;
}[] = [
  { key: 'CLINICAL', label: ACFR_CATEGORY_LABELS.CLINICAL, showParticipant: true, showCareM: false },
  { key: 'INDEPENDENCE', label: ACFR_CATEGORY_LABELS.INDEPENDENCE, showParticipant: true, showCareM: false },
  { key: 'EVERYDAY_LIVING', label: ACFR_CATEGORY_LABELS.EVERYDAY_LIVING, showParticipant: true, showCareM: false },
  { key: 'CARE_MGMT_MAIN', label: ACFR_CATEGORY_LABELS.CARE_MGMT_MAIN, showParticipant: true, showCareM: false },
  { key: 'RESTORATIVE_CARE', label: ACFR_CATEGORY_LABELS.RESTORATIVE_CARE, showParticipant: true, showCareM: true },
  { key: 'END_OF_LIFE', label: ACFR_CATEGORY_LABELS.END_OF_LIFE, showParticipant: true, showCareM: true },
  { key: 'ATHM', label: ACFR_CATEGORY_LABELS.ATHM, showParticipant: true, showCareM: false },
];

const DASH = '-';

const rowTotal = (data: AcfrReportResponse, key: keyof AcfrReportResponse['income']): number => {
  const bucket = data.income[key];
  return bucket.commonwealth + bucket.participant;
};

const careMTotal = (data: AcfrReportResponse): number => {
  const rc = data.income.RESTORATIVE_CARE as { care_mgmt: number };
  const el = data.income.END_OF_LIFE as { care_mgmt: number };
  return (rc.care_mgmt ?? 0) + (el.care_mgmt ?? 0);
};

const reconCommonwealthTotal = (data: AcfrReportResponse): number =>
  data.reconciliation_status.current.commonwealth +
  data.reconciliation_status.amended.commonwealth +
  data.reconciliation_status.adjusted.commonwealth;

const reconParticipantTotal = (data: AcfrReportResponse): number =>
  data.reconciliation_status.current.participant +
  data.reconciliation_status.amended.participant +
  data.reconciliation_status.adjusted.participant;

const exportCsv = (data: AcfrReportResponse, dateFrom: string, dateTo: string): void => {
  const fmt = (n: number) => n.toFixed(2);
  const na = '';

  const headers = ['Service Category', 'Commonwealth Subsidy', 'Participant Contribution', 'Care Management', 'Total'];

  const rows: string[][] = INCOME_ROWS.map(({ key, label, showParticipant, showCareM }) => {
    const bucket = data.income[key];
    const total = rowTotal(data, key);
    const careM = showCareM ? fmt((data.income[key] as { care_mgmt?: number }).care_mgmt ?? 0) : na;
    return [label, fmt(bucket.commonwealth), showParticipant ? fmt(bucket.participant) : na, careM, fmt(total)];
  });

  rows.push(['Participant top-ups & private clients', na, na, na, fmt(data.participant_topups)]);
  rows.push(['Other income', na, na, na, fmt(data.other_income)]);
  rows.push([
    'Total',
    fmt(data.totals.commonwealth),
    fmt(data.totals.participant),
    fmt(careMTotal(data)),
    fmt(data.totals.total_income),
  ]);

  const reconHeaders = ['Reconciliation Status', 'Items', 'Commonwealth', 'Participant'];
  const reconRows: string[][] = [
    ['Reconciled - current', String(data.reconciliation_status.current.count), fmt(data.reconciliation_status.current.commonwealth), fmt(data.reconciliation_status.current.participant)],
    ['Reconciled - amended', String(data.reconciliation_status.amended.count), fmt(data.reconciliation_status.amended.commonwealth), fmt(data.reconciliation_status.amended.participant)],
    ['Reconciled - adjusted', String(data.reconciliation_status.adjusted.count), fmt(data.reconciliation_status.adjusted.commonwealth), fmt(data.reconciliation_status.adjusted.participant)],
    [
      'Total',
      String(data.reconciliation_status.current.count + data.reconciliation_status.amended.count + data.reconciliation_status.adjusted.count),
      fmt(reconCommonwealthTotal(data)),
      fmt(reconParticipantTotal(data)),
    ],
  ];

  const toLine = (r: string[]) => r.map((cell) => `"${cell}"`).join(',');
  const csv = [
    [headers, ...rows].map(toLine).join('\n'),
    '',
    [reconHeaders, ...reconRows].map(toLine).join('\n'),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `ACFR_Report_${dateFrom}_${dateTo}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ACFRReport: React.FC<{ isActive?: boolean }> = () => {
  const [dateFrom, setDateFrom] = useState(DEFAULT_DATE_FROM);
  const [dateTo, setDateTo] = useState(DEFAULT_DATE_TO);

  const [fetchReport, { data, isFetching, isError, error, isUninitialized }] = useLazyGetAcfrReportQuery();

  const handleRunReport = useCallback(() => {
    fetchReport({ date_from: dateFrom, date_to: dateTo });
  }, [fetchReport, dateFrom, dateTo]);

  const handleExportCsv = useCallback(() => {
    if (data) exportCsv(data, dateFrom, dateTo);
  }, [data, dateFrom, dateTo]);

  const isEmpty = data && data.totals.total_income === 0 && data.unmapped_count === 0;

  return (
    <div className="p-2">
      <ContainerBanner
        className="mb-3 border rounded"
        color="light"
        icon={faInfoCircle}
        dataCy="acfr-notice-bar"
        messages={['Accrual basis (service date) · Includes reconciled claims and unclaimed supplier invoices · ACFR due 31 October 2026']}
      />

      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap" data-cy="acfr-report-controls">
        <DateInput
          type="date"
          id="acfr-date-from"
          name="date_from"
          value={dateFrom}
          placeholder="Date from"
          dataCy="acfr-report"
          onChange={(_id, val) => val && setDateFrom(val)}
        />
        <DateInput
          type="date"
          id="acfr-date-to"
          name="date_to"
          value={dateTo}
          placeholder="Date to"
          dataCy="acfr-report"
          onChange={(_id, val) => val && setDateTo(val)}
        />
        <Button
          color="primary"
          size="sm"
          onClick={handleRunReport}
          disabled={isFetching}
          dataCy="acfr-report-run-button"
          aria-label="Run Report"
        >
          {isFetching ? <Spinner loading type="beat" size={8} /> : 'Run Report'}
        </Button>
        {data && (
          <Button color="success" size="sm" onClick={handleExportCsv} dataCy="acfr-report-export-button">
            <Icon icon={faFileCsv} className="me-2" />
            Export CSV
          </Button>
        )}
      </div>

      {isError && <ErrorResponse message={getErrorMessage(error)} />}

      {data && data.unmapped_count > 0 && (
        <ContainerBanner
          className="mb-3 border rounded"
          color="warning"
          icon={faExclamationTriangle}
          dataCy="acfr-unmapped-warning"
          messages={[
            `${data.unmapped_count} claim items could not be categorised. Check that all items have a budget and service type assigned before submitting your ACFR.`,
          ]}
        />
      )}

      {isFetching && (
        <div className="text-center py-5" data-cy="acfr-report-loading">
          <Spinner loading type="beat" size={12} />
        </div>
      )}

      {!isFetching && data && (
        <>
          <Row className="mb-4 g-3">
            <Col md={3}>
              <SummaryStatCard
                count={formatCurrency(data.totals.commonwealth)}
                countLabel="Total Commonwealth"
                isLoading={isFetching}
              />
            </Col>
            <Col md={3}>
              <SummaryStatCard
                count={formatCurrency(data.totals.participant)}
                countLabel="Total Participant"
                isLoading={isFetching}
              />
            </Col>
            <Col md={3}>
              <SummaryStatCard
                count={formatCurrency(data.totals.total_income)}
                countLabel="Total Income"
                isLoading={isFetching}
              />
            </Col>
            <Col md={3}>
              <SummaryStatCard
                count={data.unmapped_count}
                countLabel="Unmapped Items"
                countClassName={data.unmapped_count > 0 ? 'text-warning' : 'text-muted'}
                isLoading={isFetching}
              />
            </Col>
          </Row>

          {isEmpty ? (
            <div className="text-center text-muted py-5" data-cy="acfr-report-empty">
              No reconciled items found for the selected date range.
            </div>
          ) : (
            <Table bordered responsive striped size="sm" className="acfr-income-table" data-cy="acfr-income-table">
              <thead className="table-dark">
                <tr>
                  <th>Service Category</th>
                  <th className="text-end">Commonwealth Subsidy</th>
                  <th className="text-end">Participant Contribution</th>
                  <th className="text-end">Care Management</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {INCOME_ROWS.map(({ key, label, showParticipant, showCareM }) => {
                  const bucket = data.income[key];
                  const total = rowTotal(data, key);
                  const careMValue = showCareM
                    ? formatCurrency((data.income[key] as { care_mgmt?: number }).care_mgmt ?? 0)
                    : DASH;
                  return (
                    <tr key={key}>
                      <td>{label}</td>
                      <td className="text-end">{formatCurrency(bucket.commonwealth)}</td>
                      <td className="text-end">{showParticipant ? formatCurrency(bucket.participant) : DASH}</td>
                      <td className="text-end">{careMValue}</td>
                      <td className="text-end">{formatCurrency(total)}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td>Participant top-ups &amp; private clients</td>
                  <td className="text-end">{DASH}</td>
                  <td className="text-end">{DASH}</td>
                  <td className="text-end">{DASH}</td>
                  <td className="text-end">{formatCurrency(data.participant_topups)}</td>
                </tr>
                <tr>
                  <td>Other income</td>
                  <td className="text-end">{DASH}</td>
                  <td className="text-end">{DASH}</td>
                  <td className="text-end">{DASH}</td>
                  <td className="text-end">{formatCurrency(data.other_income)}</td>
                </tr>
              </tbody>
              <tfoot className="table-secondary fw-bold">
                <tr>
                  <td>Total</td>
                  <td className="text-end">{formatCurrency(data.totals.commonwealth)}</td>
                  <td className="text-end">{formatCurrency(data.totals.participant)}</td>
                  <td className="text-end">{formatCurrency(careMTotal(data))}</td>
                  <td className="text-end">{formatCurrency(data.totals.total_income)}</td>
                </tr>
              </tfoot>
            </Table>
          )}

          <Table bordered responsive size="sm" className="acfr-recon-status-table mt-4" data-cy="acfr-recon-status-table">
            <thead className="table-dark">
              <tr>
                <th>Reconciliation Status</th>
                <th className="text-end">Items</th>
                <th className="text-end">Commonwealth</th>
                <th className="text-end">Participant</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Reconciled - current</td>
                <td className="text-end">{data.reconciliation_status.current.count}</td>
                <td className="text-end">{formatCurrency(data.reconciliation_status.current.commonwealth)}</td>
                <td className="text-end">{formatCurrency(data.reconciliation_status.current.participant)}</td>
              </tr>
              <tr>
                <td>Reconciled - amended</td>
                <td className="text-end">{data.reconciliation_status.amended.count}</td>
                <td className="text-end">{formatCurrency(data.reconciliation_status.amended.commonwealth)}</td>
                <td className="text-end">{formatCurrency(data.reconciliation_status.amended.participant)}</td>
              </tr>
              <tr>
                <td>Reconciled - adjusted</td>
                <td className="text-end">{data.reconciliation_status.adjusted.count}</td>
                <td className="text-end">{formatCurrency(data.reconciliation_status.adjusted.commonwealth)}</td>
                <td className="text-end">{formatCurrency(data.reconciliation_status.adjusted.participant)}</td>
              </tr>
            </tbody>
            <tfoot className="table-secondary fw-bold">
              <tr>
                <td>Total</td>
                <td className="text-end">
                  {data.reconciliation_status.current.count +
                    data.reconciliation_status.amended.count +
                    data.reconciliation_status.adjusted.count}
                </td>
                <td className="text-end">{formatCurrency(reconCommonwealthTotal(data))}</td>
                <td className="text-end">{formatCurrency(reconParticipantTotal(data))}</td>
              </tr>
            </tfoot>
          </Table>

          <p className="text-muted small mt-2" data-cy="acfr-footer-note">
            Amendment and adjustment amounts resolved using statement engine logic - previous/original entitlement types
            excluded from totals.
          </p>
        </>
      )}

      {isUninitialized && !isFetching && (
        <div className="text-center text-muted py-5" data-cy="acfr-report-uninitialised">
          Select a date range and click <strong>Run Report</strong> to generate the ACFR summary.
        </div>
      )}
    </div>
  );
};

export default ACFRReport;
